import { useGetQuery, type UseGetQueryOptions } from "@src/appV2/api";
import { API_TYPES } from "@src/appV2/api/apiTypes";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { shiftLogSchema } from "@src/appV2/ShiftLogs/api/schema";
import { type ShiftCancellationReasonTypes } from "@src/appV2/ShiftLogs/api/ShiftCancellationReasonTypes";
import { workerSchema } from "@src/appV2/Worker/api/schema";
import { agentProfileSchema } from "@src/appV2/Worker/types";
import qs from "qs";
import { z } from "zod";

import {
  baseRelationshipsSchema,
  baseShiftDataSchema,
  getResponseAttributesSchema,
} from "./schema";

export interface ShiftCancellationReasonType {
  type: ShiftCancellationReasonTypes;
  description: string;
}

export const workplaceSchema = z.object({
  type: z.literal(API_TYPES.workplace),
  id: z.string(),
  attributes: z
    .object({
      name: z.string().optional(),
      timezone: z.string().optional(),
    })
    .optional(),
});

const shiftDataSchema = baseShiftDataSchema.extend({
  attributes: getResponseAttributesSchema.optional(),
  relationships: baseRelationshipsSchema
    .extend({
      worker: z
        .object({
          data: z.object({
            type: z.literal(API_TYPES.worker),
            id: z.string(),
          }),
        })
        .optional(),
      shiftLog: z
        .object({
          data: z.array(
            z.object({
              type: z.literal(API_TYPES.shiftLog),
              id: z.string(),
            })
          ),
        })
        .optional(),
    })
    .optional(),
});

const includedWorkerSchema = workerSchema.extend({
  attributes: agentProfileSchema.pick({ name: true, profileImageUrl: true }).partial(),
});

export type IncludedWorkerSchema = z.infer<typeof includedWorkerSchema>;
export type IncludedWorkplaceSchema = z.infer<typeof workplaceSchema>;

export const shiftsListResponseSchema = z.object({
  data: z.array(shiftDataSchema),
  included: z
    .array(z.discriminatedUnion("type", [includedWorkerSchema, workplaceSchema, shiftLogSchema]))
    .optional(),
  meta: z.object({
    totalCount: z.number(),
    pageSize: z.number().optional(),
  }),
  links: z
    .object({
      next: z.string().optional(),
      prev: z.string().optional(),
      nextCursor: z.string().optional(),
      prevCursor: z.string().optional(),
    })
    .optional(),
});

export type ShiftData = z.infer<typeof shiftDataSchema>;
export type ShiftsListResponse = z.infer<typeof shiftsListResponseSchema>;

const validShiftFields = [
  "schedule.startAt",
  "schedule.endAt",
  "schedule.timeSlot",
  "schedule.extendBookabilityTo",
  "schedule.durationWithoutBreaksInHours",
  "requirements.qualificationName",
  "charges.total",
  "pay.total",
  "refillInfo.urgencyLevel",
  "refillInfo.previousWorker",
  "refillInfo.isUrgentlyBooked",
  "createdAt",
  "clockInOut.start",
  "clockInOut.end",
  "lunchInOut.start",
  "lunchInOut.end",
  "postedBy.name",
  "postedBy.userType",
  "postedBy.userId",
  "note",
  "attendance.arrivedFacilityGeofenceAt",
  "attendance.leftFacilityGeofenceAt",
] as const;

const validWorkplaceFields = ["name", "timezone"] as const;
const validWorkerFields = ["name", "profileImageUrl"] as const;
const validShiftLogFields = [
  "actionDetails.action",
  "actionDetails.subAction",
  "actionDetails.performedBy",
  "actionDetails.reason",
  "actionDetails.notes",
  "rateChangeReasons",
  "leadTime",
  "pay",
  "searchMode",
  "eventAt",
  "expectedArrival",
  "clockInAt",
  "workerType",
  "workerName",
  "timeChangeAttributes",
  "originalStart",
  "originalEnd",
] as const;

export const validShiftSortFields = [
  "schedule.startAt",
  "schedule.endAt",
  "pay.total",
  "createdAt",
  "clockInOut.start",
  "clockInOut.end",
  "_id",
] as const;

const validIncludeValues = [API_TYPES.workplace, API_TYPES.worker, API_TYPES.shiftLog] as const;

export const shiftListQuerySchema = z.object({
  filter: z
    .object({
      id: z.array(z.string()).optional(),
      facilityId: z.string().optional(),
      agentId: z.string().optional(),
      start: z
        .object({
          lt: z.coerce.date().optional(),
          gt: z.coerce.date().optional(),
        })
        .optional(),
      end: z
        .object({
          lt: z.coerce.date().optional(),
        })
        .optional(),
    })
    .optional(),
  fields: z
    .object({
      shift: z.array(z.enum(validShiftFields)).optional(),
      workplace: z.array(z.enum(validWorkplaceFields)).optional(),
      worker: z.array(z.enum(validWorkerFields)).optional(),
      shiftLog: z.array(z.enum(validShiftLogFields)).optional(),
    })
    .optional(),
  include: z
    .array(z.enum(validIncludeValues))
    .refine((items) => new Set(items).size === items.length, "Include values must be unique")
    .optional(),
  sort: z
    .object({
      field: z.enum([...validShiftSortFields]),
      order: z.enum(["asc", "desc"]).default("desc"),
    })
    .optional(),
  page: z
    .object({
      size: z.number().optional(),
      // Offset in number as a string
      cursor: z.string().optional(),
    })
    .optional(),
});

export type ShiftListQuery = z.infer<typeof shiftListQuerySchema>;

export function useGetShifts(
  params: ShiftListQuery,
  options: UseGetQueryOptions<ShiftsListResponse> = {}
) {
  return useGetQuery({
    url: `${environmentConfig.REACT_APP_BASE_API_URL}/v3/shifts`,
    requestSchema: shiftListQuerySchema,
    responseSchema: shiftsListResponseSchema,
    queryParams: params,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_SHIFTS_LIST_FAILURE,
    },
    paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "comma" }),
    ...options,
  });
}
